import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import estimateExperienceLevelFromQualifications from '../../utils/estimateExperience';
import api from '../../api/axios';
import enhancedApi from '../../utils/enhancedApi';
import { jobScoresCache } from '../../utils/jobScoresCache';
import { performanceMonitor } from '../../utils/performanceMonitor';
import { calculateOverallJobScore } from '../../utils/jobScoreCalculator';

import { fetchReferralScore } from '../ContactSlice/contactSlice';
import { clientDataCache, getCurrentUserIdFromStorage } from '../../utils/clientDataCache';

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const normalizeKeyText = (value) => normalizeText(value).replace(/\s+/g, ' ');

const getStableId = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (typeof value !== 'object') return '';

    if (value.$oid) return String(value.$oid);
    if (value.$id) return String(value.$id);
    if (value.$numberLong) return String(value.$numberLong);
    if (value._id) return getStableId(value._id);

    return '';
};

const looksLikeDescriptionTitle = (value) => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text) return true;
    
    // If it's very long, it's likely a description block
    if (text.length > 180) return true;

    const badPatterns = /(^duties\s*:|^responsibilities\s*:|position requirements|apply now|email address|search filter|filter results|job alerts|submit resume)/i;
    return badPatterns.test(text);
};

const extractTitleFromMess = (value) => {
    let text = String(value || '').trim();
    if (!text) return '';

    // Remove common UI artifacts from scraped data
    text = text.replace(/^Show Description/i, '').trim();

    // If it looks like a description (too long), try to take just the first portion
    if (text.length > 140) {
        // Try to find the first line or a sentence break
        const firstLine = text.split(/\r?\n/)[0].trim();
        if (firstLine && firstLine.length <= 140 && firstLine.length >= 5) {
            return firstLine;
        }

        // Try to take the first portion before common location/type markers if they are smashed together
        // Split on common markers or transition from lowercase/digit/closing-paren to Uppercase
        const commonMarkers = /(?:\s+at\s+[\w\s]+|\s+in\s+[\w\s]+|\s+-\s+|[a-z0-9)](?=[A-Z])|\d{4,})/;
        const firstPortion = text.split(commonMarkers)[0].trim();
        if (firstPortion && firstPortion.length <= 140 && firstPortion.length >= 5) {
            return firstPortion;
        }

        return text.slice(0, 100).trim() + '...';
    }

    return text;
};

const getJobKey = (job) => {
    const directId = getStableId(job?._id) || getStableId(job?.id) || getStableId(job?.job_id) || getStableId(job?.jobId);
    if (directId) return String(directId);

    return getJobCompositeKey(job);
};

const getJobCompositeKey = (job) => {
    const title = normalizeKeyText(job?.jobPosition || job?.title || job?.jobTitle);
    const company = normalizeKeyText(job?.companyName || job?.company);
    const location = normalizeKeyText(job?.jobLocation);
    return `${title}::${company}::${location}`;
};

const getJobLooseCompositeKey = (job) => {
    const title = normalizeKeyText(job?.jobPosition || job?.title || job?.jobTitle);
    const company = normalizeKeyText(job?.companyName || job?.company);
    return `${title}::${company}`;
};

const getJobCandidateKeys = (job) => {
    const keys = [
        getJobKey(job),
        getJobCompositeKey(job),
        getJobLooseCompositeKey(job)
    ].filter(Boolean);

    return Array.from(new Set(keys));
};

const sanitizeJobTitle = (job) => {
    const titleCandidates = [job?.jobPosition, job?.title, job?.jobTitle].filter(Boolean);
    
    for (let i = 0; i < titleCandidates.length; i += 1) {
        const rawCandidate = String(titleCandidates[i]).trim();
        if (!rawCandidate) continue;

        // Try to extract a clean title first
        const extracted = extractTitleFromMess(rawCandidate);
        
        // If the extracted title looks valid (not too long and not matching bad patterns)
        if (extracted && !looksLikeDescriptionTitle(extracted)) {
            return extracted;
        }
    }

    return 'Job Position Not Available';
};

const sanitizeJobs = (jobs) => {
    if (!Array.isArray(jobs)) return [];
    return jobs.map((job) => ({
        ...job,
        jobPosition: sanitizeJobTitle(job)
    }));
};

const dedupeJobs = (jobs) => {
    const jobMap = new Map();
    for (let i = 0; i < jobs.length; i += 1) {
        const job = jobs[i];
        const key = getJobKey(job);
        if (!key) continue;
        if (!jobMap.has(key)) jobMap.set(key, job);
    }
    return Array.from(jobMap.values());
};

const getRankingFields = (rankedJob) => ({
    score: rankedJob?.score,
    scoreBreakdown: rankedJob?.scoreBreakdown,
    competitionLabel: rankedJob?.competitionLabel,
    matchLabel: rankedJob?.matchLabel,
    bestRoleMatch: rankedJob?.bestRoleMatch || null,
    trackType: rankedJob?.trackType || null,
    roleMatches: Array.isArray(rankedJob?.roleMatches) ? rankedJob.roleMatches : [],
    reasons: rankedJob?.reasons,
    reasonCodes: rankedJob?.reasonCodes,
    signalEvidence: rankedJob?.signalEvidence,
    descriptionSections: rankedJob?.descriptionSections,
    jdSummary: rankedJob?.jdSummary,
    rankingConfidence: rankedJob?.scoreBreakdown?.sourceConfidence ?? null
});

const getCompanyIdValue = (job) => {
    if (!job?.companyId) return '';
    if (typeof job.companyId === 'string') return job.companyId;
    if (typeof job.companyId === 'object') {
        if (job.companyId._id) return getStableId(job.companyId._id) || String(job.companyId._id);
        if (job.companyId.$oid) return String(job.companyId.$oid);
    }
    return '';
};

// Check whether a scores object (from DB or populated companyId) has at least one
// category with a valid numeric weightedScore > 0.
const hasUsableScores = (scores) => {
    if (!scores || typeof scores !== 'object') return false;
    
    // We are more lenient now: if even one major category has a score, we show it.
    const keys = ['compensation', 'stability', 'careerGrowth', 'growth', 'workLifeBalance', 'WLB', 'toxicity', 'referral'];
    return keys.some((key) => {
        const category = scores[key];
        if (!category) return false;
        
        // Handle both DB structure (category.weightedScore) and UI structure (direct number)
        const scoreValue = typeof category === 'object' ? category.weightedScore : category;
        return Number.isFinite(Number(scoreValue)) && Number(scoreValue) > 0;
    });
};

const buildCompanyScores = (scores) => ({
    compensation: scores.compensation?.weightedScore,
    stability: scores.stability?.weightedScore,
    growth: scores.careerGrowth?.weightedScore,
    WLB: scores.workLifeBalance?.weightedScore,
    toxicity: scores.toxicity?.weightedScore,
    referral: scores.referral?.weightedScore,
});

const enrichJob = (job, companies) => {
    const companyIdValue = getCompanyIdValue(job);
    let companyData = companies.find((company) => String(company._id) === companyIdValue);

    // ✅ RESILIENT LOOKUP: If ID match fails, try matching by name
    if (!companyData && job.companyName) {
        // Prefer company with scores if multiple exist
        companyData = companies.find((c) => c.name === job.companyName && c.scores) || 
                      companies.find((c) => c.name === job.companyName);
    }
    const incomingScoreStatus = String(companyData?.scoresStatus || job.companyId?.scoresStatus || '').toLowerCase();

    let scoresStatus = 'pending';
    let companyScores = null;

    if (incomingScoreStatus === 'processing') {
        return {
            ...job,
            scoresStatus: 'processing',
            companyScores: null,
            overallScore: 0,
            overallScoreStatus: 'processing'
        };
    }

    if (companyData && companyData.scores) {
        if (companyData.scores === 'failed') {
            scoresStatus = 'failed';
        } else if (hasUsableScores(companyData.scores)) {
            scoresStatus = 'success';
            companyScores = buildCompanyScores(companyData.scores);
            // Valid scores found, fall through to central return
        }
        // else: company found in companies array but scores are empty/zero → fall through
        // to job.companyId.scores fallback below so previously-valid scores are preserved.
    }

    // Fallback: try populated companyId.scores (set by /api/jobs/fetchjobs or /api/jobs/ranked)
    if (job.companyId?.scores === 'failed') {
        scoresStatus = 'failed';
    } else if (job.companyId?.scores && hasUsableScores(job.companyId.scores)) {
        scoresStatus = job.companyId.scoresStatus || 'success';
        companyScores = buildCompanyScores(job.companyId.scores);
    } else if (job.companyId?.scoresStatus) {
        scoresStatus = job.companyId.scoresStatus;
    } else if (companyData) {
        // Company exists in DB but has no scores yet
        scoresStatus = companyData.scoresStatus || 'pending';
    }

    // Final sanity check: if status is success but we have zero scores, revert to pending
    // This happens when database says 'success' but scores object is empty or zeros.
    if (scoresStatus === 'success' && !hasUsableScores(companyScores || {})) {
        scoresStatus = 'pending';
    }

    const overallScoreResult = calculateOverallJobScore(companyScores || {});

    return {
        ...job,
        scoresStatus,
        companyScores,
        overallScore: overallScoreResult.score,
        overallScoreStatus: overallScoreResult.status
    };
};

// Fetch a single job by id (with the user's score) — used by the job detail page
// for jobs that aren't in the in-memory feed (e.g. older saved jobs).
export const fetchJobById = createAsyncThunk('jobs/fetchJobById', async (jobId, thunkAPI) => {
    try {
        const res = await api.get(`/api/jobs/job/${jobId}`);
        return res.data?.job || res.data;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || 'Failed to load job');
    }
});

export const fetchUserJobs = createAsyncThunk('jobs/fetchUserJobs', async (_, thunkAPI) => {
    try {
        // Load the user's ENTIRE list — no ceiling. The backend caps each request at 1000, so
        // page through with skip until it reports no more. This keeps the client-side
        // filters/sorts/counts/pagination correct at any job count (they run over the full set).
        const PAGE_SIZE = 1000;
        let baseJobsArray = [];
        let totalCount = 0;
        for (let skip = 0, guard = 0; guard < 500; skip += PAGE_SIZE, guard += 1) {
            const pageResp = await api.get(`/api/jobs/fetchjobs?limit=${PAGE_SIZE}&skip=${skip}`);
            const pageData = pageResp?.data;
            const batch = Array.isArray(pageData?.jobs) ? pageData.jobs : (Array.isArray(pageData) ? pageData : []);
            baseJobsArray = baseJobsArray.concat(batch);
            totalCount = pageData?.pagination?.total ?? baseJobsArray.length;
            const hasMore = pageData?.pagination?.hasMore ?? (batch.length === PAGE_SIZE);
            if (!hasMore || batch.length === 0) break;
        }
        // True total drives the "All" chip / header / footer so they reflect reality.
        thunkAPI.dispatch(setTotalJobs(totalCount || baseJobsArray.length));
        // Do NOT call `/api/jobs/ranked` on startup — this can return external-only
        // jobs (RapidAPI fallback) that are not persisted in DB and cause ghost jobs.
        // Ranked data will be populated when the user explicitly requests a fetch.
        let rankedJobsArray = [];
        let rankedPayload = null;
        let rankedResponseSucceeded = false;
        // Clear any ranked context on startup
        thunkAPI.dispatch(setRankedContext({ signals: null, meta: null, roleGroups: [] }));

        const rankedMap = new Map();
        for (let i = 0; i < rankedJobsArray.length; i += 1) {
            const rankedJob = rankedJobsArray[i];
            const keys = getJobCandidateKeys(rankedJob);
            for (let j = 0; j < keys.length; j += 1) {
                rankedMap.set(keys[j], rankedJob);
            }
        }

        const baseMap = new Map();
        for (let i = 0; i < baseJobsArray.length; i += 1) {
            const baseJob = baseJobsArray[i];
            const keys = getJobCandidateKeys(baseJob);
            for (let j = 0; j < keys.length; j += 1) {
                if (!baseMap.has(keys[j])) {
                    baseMap.set(keys[j], baseJob);
                }
            }
        }

        let matchedRankedJobs = 0;
        const mergedJobs = baseJobsArray.map((job) => {
            const keys = getJobCandidateKeys(job);
            let rankedJob = null;

            for (let i = 0; i < keys.length; i += 1) {
                const candidate = rankedMap.get(keys[i]);
                if (candidate) {
                    rankedJob = candidate;
                    break;
                }
            }

            if (!rankedJob) {
                return {
                    ...job,
                    rankingMeta: {
                        rankedEndpointAvailable: rankedResponseSucceeded,
                        hasRankedData: false
                    }
                };
            }

            const mergedCompanyId = rankedJob?.companyId?.scores
                ? rankedJob.companyId
                : (job.companyId?.scores ? job.companyId : (rankedJob.companyId || job.companyId));

            matchedRankedJobs += 1;
            return {
                ...job,
                companyId: mergedCompanyId,
                ...getRankingFields(rankedJob),
                rankingMeta: {
                    rankedEndpointAvailable: rankedResponseSucceeded,
                    hasRankedData: true
                }
            };
        });

        if (rankedResponseSucceeded && rankedJobsArray.length > 0 && matchedRankedJobs === 0) {
            console.warn('[jobs/fetchUserJobs] ranked data returned but no cards matched. baseJobs:', baseJobsArray.length, 'rankedJobs:', rankedJobsArray.length);
        }

        const rankedPrimaryJobs = rankedJobsArray.map((rankedJob) => {
            const keys = getJobCandidateKeys(rankedJob);
            let baseJob = null;

            for (let i = 0; i < keys.length; i += 1) {
                const candidate = baseMap.get(keys[i]);
                if (candidate) {
                    baseJob = candidate;
                    break;
                }
            }

            const mergedCompanyId = rankedJob?.companyId?.scores
                ? rankedJob.companyId
                : (baseJob?.companyId?.scores ? baseJob.companyId : (rankedJob.companyId || baseJob?.companyId));

            return {
                ...(baseJob || rankedJob),
                companyId: mergedCompanyId,
                ...getRankingFields(rankedJob),
                rankingMeta: {
                    rankedEndpointAvailable: rankedResponseSucceeded,
                    hasRankedData: true
                }
            };
        });

        // Always prefer DB-backed jobs on startup. Do not use ranked primary payload
        // as the source-of-truth to avoid introducing non-persisted external jobs.
        const jobSource = mergedJobs.length > 0 ? mergedJobs : [];

        const jobsArray = dedupeJobs(sanitizeJobs(jobSource));
        
        const state = thunkAPI.getState();
        const companies = state.companies.companies;
        
        // OPTIMIZED: Use Map for O(1) lookup instead of O(n) find()
        const companyMap = new Map();
        if (companies && companies.length > 0) {
            companies.forEach(company => {
                if (company._id) {
                    companyMap.set(company._id.toString(), company);
                }
            });
        }
        
        // Enrich jobs with optimized lookup
        const enrichedJobs = jobsArray.map(job => {
            // Use Map lookup for faster access
            const companyId = getCompanyIdValue(job);
            const companyData = companyId ? companyMap.get(companyId) : null;
            return enrichJob(job, companyData ? [companyData] : []);
        });
        
        return enrichedJobs;
    } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to fetch user jobs';
        return thunkAPI.rejectWithValue(message);
    }
});

export const fetchNewJobs = createAsyncThunk('jobs/fetchNewJobs', async (arg, thunkAPI) => {
    try {
        const limit = Number(arg?.limit) > 0 ? Number(arg.limit) : undefined;
        const postedWithinHours = Number(arg?.postedWithinHours) > 0 ? Number(arg.postedWithinHours) : undefined;
        const { data } = await api.post('/api/jobs/fetch-new-jobs', {
            ...(limit ? { limit } : {}),
            ...(postedWithinHours ? { postedWithinHours } : {})
        });
        let rankedJobsArray = [];
        let rankedPayload = null;
        let rankedResponseSucceeded = false;

        try {
            const rankedResponse = await api.get('/api/jobs/ranked');
            rankedPayload = rankedResponse?.data || null;
            rankedJobsArray = Array.isArray(rankedPayload?.jobs) ? rankedPayload.jobs : [];
            rankedResponseSucceeded = true;
            thunkAPI.dispatch(setRankedContext({
                signals: rankedPayload?.signals || null,
                meta: rankedPayload?.meta || null,
                roleGroups: Array.isArray(rankedPayload?.roleGroups) ? rankedPayload.roleGroups : []
            }));
        } catch (rankedError) {
            rankedJobsArray = [];
            rankedResponseSucceeded = false;
            thunkAPI.dispatch(setRankedContext({ signals: null, meta: null, roleGroups: [] }));
            console.error('[jobs/fetchNewJobs] ranked endpoint failed:', rankedError?.response?.data?.message || rankedError?.message || rankedError);
        }

        const rankedMap = new Map();
        for (let i = 0; i < rankedJobsArray.length; i += 1) {
            const rankedJob = rankedJobsArray[i];
            const keys = getJobCandidateKeys(rankedJob);
            for (let j = 0; j < keys.length; j += 1) {
                rankedMap.set(keys[j], rankedJob);
            }
        }
        
        // Backend now returns: { success, jobs, newCount, totalFetched, message }
        const state = thunkAPI.getState();
        const companies = state.companies.companies;
        
        // Enrich the new jobs with ranked metadata and company scores
        const withRanking = (data.jobs || []).map((job) => {
            const keys = getJobCandidateKeys(job);
            let rankedJob = null;

            for (let i = 0; i < keys.length; i += 1) {
                const candidate = rankedMap.get(keys[i]);
                if (candidate) {
                    rankedJob = candidate;
                    break;
                }
            }

            if (!rankedJob) {
                return {
                    ...job,
                    rankingMeta: {
                        rankedEndpointAvailable: rankedResponseSucceeded,
                        hasRankedData: false
                    }
                };
            }

            const mergedCompanyId = rankedJob?.companyId?.scores
                ? rankedJob.companyId
                : (job.companyId?.scores ? job.companyId : (rankedJob.companyId || job.companyId));

            return {
                ...job,
                companyId: mergedCompanyId,
                ...getRankingFields(rankedJob),
                rankingMeta: {
                    rankedEndpointAvailable: true,
                    hasRankedData: true
                }
            };
        });

        const cleanJobs = dedupeJobs(sanitizeJobs(withRanking));
        const enrichedJobs = cleanJobs.map(job => enrichJob(job, companies));
        
        return {
            jobs: enrichedJobs,
            newCount: data.newCount,
            totalFetched: data.totalFetched,
            liveSearchUnavailable: data.liveSearchUnavailable || false,
            message: data.message
        };
    } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to fetch new jobs';
        return thunkAPI.rejectWithValue(message);
    }
});

// Post-onboarding initial fetch: pull the latest ATS jobs matched to the user's
// profile preferences (free, served from our own scraped ATS index — no JSearch call),
// then reload the full job list so the new jobs land enriched and ranked.
export const fetchATSJobsByPreference = createAsyncThunk('jobs/fetchATSJobsByPreference', async (arg, thunkAPI) => {
    try {
        const limit = Number(arg?.limit) > 0 ? Number(arg.limit) : 50;
        const { data } = await api.post('/api/jobs/fetch-ats-jobs-only', { limit });
        await thunkAPI.dispatch(fetchUserJobs()).unwrap();
        return {
            newCount: data?.newCount || 0,
            addedToUser: data?.addedToUser || 0,
            message: data?.message
        };
    } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to fetch ATS jobs';
        return thunkAPI.rejectWithValue(message);
    }
});

export const checkJobSyncStatus = createAsyncThunk('jobs/checkJobSyncStatus', async (_, thunkAPI) => {
    try {
        const response = await api.get('/api/jobs/getsyncingstatus');

        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to check sync status';
        return thunkAPI.rejectWithValue(message);
    }
}
);

// Fetch jobs for a specific company
export const fetchJobsForCompany = createAsyncThunk('jobs/fetchJobsForCompany', async (companyId, { rejectWithValue }) => {
    try {
        const res = await api.get(`/api/jobs/by-company/${companyId}`);
        return res.data;
    } catch (err) {
        return rejectWithValue(err.response?.data?.error || err.message);
    }
}
);

export const fetchRecruiterForJob = createAsyncThunk("jobs/fetchRecruiterForJob", async ({ jobDetails, numberOfContacts }, { rejectWithValue }) => {
    try {
        console.log('🔵 [Redux] Fetching recruiter for job:', jobDetails.companyName);
        const token = localStorage.getItem("token");

        const response = await enhancedApi.post("/api/jobs/fetch-recruiter-for-job",
            { jobDetails, ...(numberOfContacts ? { numberOfContacts } : {}) },
            {
                headers: {
                    "auth-token": token
                },
                cacheTTL: 0, // Disable caching for this request
            }
        )

        // enhancedApi already returns response.data, so response is the actual data
        console.log('🟢 [Redux] API Response received:', response);
        console.log('   Has hiring_manager?', !!response?.hiring_manager);
        console.log('   Has recruiters?', !!response?.recruiters);
        console.log('   Hiring managers count:', response?.hiring_manager?.best_guesses?.length || 0);
        console.log('   Recruiters count:', response?.recruiters?.best_guesses?.length || 0);

        return response;
    } catch (error) {
        console.error('🔴 [Redux] Error fetching recruiter:', error);
        console.error('   Error response:', error.response?.data);
        if (error.response) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue({ message: 'A network error occurred' });
    }
})

// Read-only "peek": returns previously-fetched recruiter contacts for a job if
// they exist (server returns null otherwise). NEVER calls the enrichment
// provider and NEVER charges credits — used on page load/refresh to restore the
// referral path panel.
export const peekRecruiterForJob = createAsyncThunk("jobs/peekRecruiterForJob", async ({ jobDetails }, { rejectWithValue }) => {
    try {
        const token = localStorage.getItem("token");
        const response = await enhancedApi.post("/api/jobs/fetch-recruiter-for-job",
            { jobDetails, peekOnly: true },
            {
                headers: { "auth-token": token },
                cacheTTL: 0,
            }
        );
        return response; // recruiterData object, or null if nothing cached
    } catch (error) {
        if (error.response) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue({ message: 'A network error occurred' });
    }
})

const jobSlice = createSlice({
    name: 'jobs',
    initialState: {
        recommendedJobs: [],
        companyJobs: [],
        enrichedJobsArray: [],
        totalJobs: 0, // true count of the user's jobs (from backend pagination.total), not the capped loaded list
        loading: false,
        loadingMoreJobs: false, // Track loading state for additional jobs
        syncing: false,
        error: null,
        recruiterData: null,
        recruiterLoading: false,
        recruiterError: null,
        recruitersByJobId: {}, // Store recruiter data mapped by job ID
        scrollTargetJobId: null,
        rankedContext: {
            signals: null,
            meta: null,
            roleGroups: []
        }
    },
    reducers: {
        setScrollTargetJobId: (state, action) => {
            state.scrollTargetJobId = action.payload;
        },
        clearScrollTargetJobId: (state) => {
            state.scrollTargetJobId = null;
        },
        setSyncing: (state, action) => {
            state.syncing = action.payload;
        },
        setTotalJobs: (state, action) => {
            const n = Number(action.payload);
            state.totalJobs = Number.isFinite(n) && n >= 0 ? n : 0;
        },
        clearRecommendedJobs: (state) => {
            state.recommendedJobs = [];
        },
        setRankedContext: (state, action) => {
            state.rankedContext = {
                signals: action.payload?.signals || null,
                meta: action.payload?.meta || null,
                roleGroups: Array.isArray(action.payload?.roleGroups) ? action.payload.roleGroups : []
            };
        },
        clearCompanyJobs: (state) => {
            state.companyJobs = [];
            state.error = null;
        },
        // Progressive score loading - update jobs with scores when companies are available
        updateJobsWithScores: (state, action) => {
            const companies = action.payload.companies;
            if (!companies || !state.enrichedJobsArray.length) return;

            console.log('🔄 Updating jobs with company scores...');
            state.enrichedJobsArray = state.enrichedJobsArray.map(job => enrichJob(job, companies));
        },
        replaceJobsFromBackend: (state, action) => {
            const incomingJobs = Array.isArray(action.payload?.jobs) ? action.payload.jobs : [];
            const companies = Array.isArray(action.payload?.companies) ? action.payload.companies : [];
            const cleanJobs = dedupeJobs(sanitizeJobs(incomingJobs));
            state.enrichedJobsArray = cleanJobs.map((job) => enrichJob(job, companies));
            const uid = getCurrentUserIdFromStorage();
            if (uid && state.enrichedJobsArray.length) {
                clientDataCache.writeJobs(uid, state.enrichedJobsArray);
            }
        },
        
        // web socket function 
        updateScoresForCompany: (state, action) => {
            const updatedCompany = action.payload;
            console.log('Received company update via WebSocket:', updatedCompany);
            if (!updatedCompany || !updatedCompany._id) return;

            const companyId = getStableId(updatedCompany._id) || String(updatedCompany._id);

            // create a new array using map.
            state.enrichedJobsArray = state.enrichedJobsArray.map(job => {
                // If this is not the job we're looking for, return it as is.
                if (getCompanyIdValue(job) !== String(companyId)) {
                    return job;
                }

                const existingCompanyObject = (job.companyId && typeof job.companyId === 'object') ? job.companyId : {};
                // This IS a job to update. Create a BRAND NEW object to guarantee a re-render.
                const newJobData = {
                    ...job,
                    companyId: {
                        ...existingCompanyObject, // Copy nested company properties when populated
                        _id: companyId,
                        name: updatedCompany.name || existingCompanyObject.name || job.companyName,
                        scores: updatedCompany.scores, // Overwrite with the new scores from the websocket
                        scoresStatus: updatedCompany.scoresStatus || existingCompanyObject.scoresStatus || 'success'
                    },
                };

                // Now, re-run the enrichment on this single new job object.
                // This ensures the `scoresStatus` and `companyScores` props are correctly updated.
                return enrichJob(newJobData, [updatedCompany]);
            });
        },
        updateJobScore: (state, action) => {
            const payload = action.payload || {};
            const jobId = String(payload.jobId || payload._id || '');
            if (!jobId) return;

            const idx = state.enrichedJobsArray.findIndex((j) => {
                const candidateId = String(getStableId(j._id) || j._id || getJobKey(j));
                return candidateId === jobId || getJobKey(j) === jobId;
            });

            if (idx === -1) return;

            const existing = state.enrichedJobsArray[idx];

            const updated = {
                ...existing,
                // Scores
                score: payload.score ?? existing.score,
                scoreBreakdown: payload.scoreBreakdown ?? existing.scoreBreakdown,
                scoreUpdatedAt: payload.scoreUpdatedAt ?? new Date().toISOString(),

                // Ranking & personalization fields
                reasons: Array.isArray(payload.reasons) ? payload.reasons : existing.reasons,
                reasonCodes: Array.isArray(payload.reasonCodes) ? payload.reasonCodes : existing.reasonCodes,
                signalEvidence: payload.signalEvidence ?? existing.signalEvidence,
                jdSummary: payload.jdSummary ?? existing.jdSummary,
                descriptionSections: payload.descriptionSections ?? existing.descriptionSections,
                roleMatches: Array.isArray(payload.roleMatches) ? payload.roleMatches : existing.roleMatches,
                bestRoleMatch: payload.bestRoleMatch ?? existing.bestRoleMatch,
                matchLabel: payload.matchLabel ?? existing.matchLabel,
                competitionLabel: payload.competitionLabel ?? existing.competitionLabel,
                rankingConfidence: payload.rankingConfidence ?? existing.rankingConfidence,
                personalizationGeneratedAt: payload.personalizationGeneratedAt ?? existing.personalizationGeneratedAt,
                personalizationSource: payload.personalizationSource ?? existing.personalizationSource,

                rankingMeta: {
                    ...(existing.rankingMeta || {}),
                    hasRankedData: true
                }
            };

            state.enrichedJobsArray[idx] = updated;
        },
        updateJobScoresBatch: (state, action) => {
            const updates = Array.isArray(action.payload) ? action.payload : [];
            if (!updates.length || !state.enrichedJobsArray.length) return;

            const updatesById = new Map();
            updates.forEach((item) => {
                const id = String(item?.jobId || item?._id || '');
                if (id) updatesById.set(id, item);
            });
            if (!updatesById.size) return;

            state.enrichedJobsArray = state.enrichedJobsArray.map((existing) => {
                const existingId = String(getStableId(existing._id) || existing._id || getJobKey(existing));
                const payload = updatesById.get(existingId) || updatesById.get(getJobKey(existing));
                if (!payload) return existing;

                return {
                    ...existing,
                    score: payload.score ?? existing.score,
                    scoreBreakdown: payload.scoreBreakdown ?? existing.scoreBreakdown,
                    scoreUpdatedAt: payload.scoreUpdatedAt ?? new Date().toISOString(),
                    reasons: Array.isArray(payload.reasons) ? payload.reasons : existing.reasons,
                    reasonCodes: Array.isArray(payload.reasonCodes) ? payload.reasonCodes : existing.reasonCodes,
                    signalEvidence: payload.signalEvidence ?? existing.signalEvidence,
                    jdSummary: payload.jdSummary ?? existing.jdSummary,
                    descriptionSections: payload.descriptionSections ?? existing.descriptionSections,
                    roleMatches: Array.isArray(payload.roleMatches) ? payload.roleMatches : existing.roleMatches,
                    bestRoleMatch: payload.bestRoleMatch ?? existing.bestRoleMatch,
                    matchLabel: payload.matchLabel ?? existing.matchLabel,
                    competitionLabel: payload.competitionLabel ?? existing.competitionLabel,
                    rankingConfidence: payload.rankingConfidence ?? existing.rankingConfidence,
                    personalizationGeneratedAt: payload.personalizationGeneratedAt ?? existing.personalizationGeneratedAt,
                    personalizationSource: payload.personalizationSource ?? existing.personalizationSource,
                    rankingMeta: {
                        ...(existing.rankingMeta || {}),
                        hasRankedData: true
                    }
                };
            });
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserJobs.pending, (state) => {
                state.loading = true;
                state.error = null;
                // Do NOT paint stale cached jobs here. Painting the cache (e.g. 60) and then
                // replacing with the fresh list (e.g. 261) caused a visible two-step / count
                // jump. Instead we keep loading=true so the UI shows a single loader (skeletons)
                // and then renders the full list once in fetchUserJobs.fulfilled.
            })
            .addCase(fetchUserJobs.fulfilled, (state, action) => {
                const next = Array.isArray(action.payload) ? action.payload : [];
                state.enrichedJobsArray = next;
                state.loading = false;
                const uid = getCurrentUserIdFromStorage();
                if (uid && next.length) {
                    clientDataCache.writeJobs(uid, next);
                }
            })
            .addCase(fetchUserJobs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                if (state.enrichedJobsArray.length === 0) {
                    const uid = getCurrentUserIdFromStorage();
                    const cached = uid ? clientDataCache.readJobs(uid) : null;
                    if (cached?.length) {
                        state.enrichedJobsArray = dedupeJobs(sanitizeJobs(cached));
                    }
                }
            })
            .addCase(fetchNewJobs.pending, (state) => {
                state.loadingMoreJobs = true;
                state.error = null;
            })
            .addCase(fetchNewJobs.fulfilled, (state, action) => {
                // New backend response: { jobs: [], newCount: 50, totalFetched: 350, message: "..." }
                const newJobs = action.payload.jobs || [];
                
                // ✅ Prepend new jobs to existing array (newest first). Do NOT cap the list —
                // capping to 100 here silently dropped the user's existing jobs (e.g. 261 → 100)
                // after a fetch, even when no new jobs were added.
                state.enrichedJobsArray = dedupeJobs(sanitizeJobs([...newJobs, ...state.enrichedJobsArray]));

                state.loadingMoreJobs = false;
                const uid = getCurrentUserIdFromStorage();
                if (uid && state.enrichedJobsArray.length) {
                    clientDataCache.writeJobs(uid, state.enrichedJobsArray);
                }
            })
            .addCase(fetchNewJobs.rejected, (state, action) => {
                state.loadingMoreJobs = false;
                state.error = action.payload;
            })
            // Post-onboarding ATS fetch: reuse the "loading more jobs" indicator; the job
            // list itself is painted by the fetchUserJobs dispatch inside the thunk.
            .addCase(fetchATSJobsByPreference.pending, (state) => {
                state.loadingMoreJobs = true;
                state.error = null;
            })
            .addCase(fetchATSJobsByPreference.fulfilled, (state) => {
                state.loadingMoreJobs = false;
            })
            .addCase(fetchATSJobsByPreference.rejected, (state, action) => {
                state.loadingMoreJobs = false;
                state.error = action.payload;
            })
            // Company-specific Jobs
            .addCase(fetchJobsForCompany.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchJobsForCompany.fulfilled, (state, action) => {
                state.companyJobs = action.payload;
                state.loading = false;
            })
            .addCase(fetchJobsForCompany.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch company jobs';
                state.companyJobs = [];
            })

            // For listening to referral change
            .addCase(fetchReferralScore.fulfilled, (state, action) => {
                const { updatedCompany } = action.payload;

                // console.log(updatedCompany)

                if (!updatedCompany) {
                    return;
                }

                const companyName = updatedCompany.name;
                const scores = updatedCompany.scores || {};
                const formattedScores = {
                    compensation: scores.compensation?.weightedScore,
                    stability: scores.stability?.weightedScore,
                    growth: scores.careerGrowth?.weightedScore,
                    WLB: scores.workLifeBalance?.weightedScore,
                    toxicity: scores.toxicity?.weightedScore,
                    referral: scores.referral?.weightedScore
                };

                // --- UPDATE THE ENRICHED JOBS ARRAY ---
                const enrichedJobIndex = state.enrichedJobsArray.findIndex(
                    (job) => job.companyName === companyName
                );

                if (enrichedJobIndex !== -1) {
                    console.log(`Updating job in enrichedJobsArray at index ${enrichedJobIndex}`);
                    const originalJob = current(state.enrichedJobsArray[enrichedJobIndex]);
                    // console.log(originalJob)
                    const updatedJob = {
                        ...originalJob,
                        companyScores: formattedScores
                    };

                    // This is the line that performs the update
                    state.enrichedJobsArray[enrichedJobIndex] = updatedJob;

                    console.log(
                        "VERIFY STATE: Job object in state AFTER update:",
                        state.enrichedJobsArray[enrichedJobIndex]
                    );
                }

                // --- ALSO UPDATE THE RECOMMENDED JOBS ARRAY ---
                const recommendedJobIndex = state.recommendedJobs.findIndex(
                    (job) => job.companyName === companyName
                );

                if (recommendedJobIndex !== -1) {
                    console.log(`Updating job in recommendedJobs at index ${recommendedJobIndex}`);
                    const originalJob = current(state.recommendedJobs[recommendedJobIndex]);
                    const updatedJob = {
                        ...originalJob,
                        companyScores: formattedScores
                    };
                    state.recommendedJobs[recommendedJobIndex] = updatedJob;

                    console.log(
                        "VERIFY STATE: Recommended job in state AFTER update:",
                        state.recommendedJobs[recommendedJobIndex]
                    );
                }
            })

            // Fetch recruiter for job
            .addCase(fetchRecruiterForJob.pending, (state) => {
                state.recruiterLoading = true;
                state.recruiterData = null;
                state.recruiterError = null;
            })
            .addCase(fetchRecruiterForJob.fulfilled, (state, action) => {
                state.recruiterLoading = false;
                state.recruiterData = action.payload;
                
                // Store recruiter data mapped by job ID for later use
                const jobId = action.meta.arg.jobDetails._id;
                if (jobId && action.payload) {
                    state.recruitersByJobId[jobId] = action.payload;
                }
            })
            .addCase(fetchRecruiterForJob.rejected, (state, action) => {
                state.recruiterLoading = false;
                state.recruiterError = action.payload;
            });
    },
});

export const { clearRecommendedJobs, setRankedContext, clearCompanyJobs, setSyncing, setTotalJobs, updateScoresForCompany, updateJobsWithScores, replaceJobsFromBackend, updateJobScore, updateJobScoresBatch, setScrollTargetJobId, clearScrollTargetJobId } = jobSlice.actions;
export default jobSlice.reducer;